import { Drawer, FormControlLabel, Grid, Switch } from "@mui/material";
import moment from "moment";
import { useEffect, useState } from "react";
import { ValidatorForm } from "react-material-ui-form-validator";
import { useNavigate, useParams } from "react-router-dom";
import { RacingLicenseGradeAutoComplete } from "../../_components/autocomplete/racingLicenseGrade.autoComplete";
import { RacingLicenseTypeAutoComplete } from "../../_components/autocomplete/racingLicenseType.autoComplete";
import { PrimaryButton } from "../../_components/button/primary.button";
import { DrawerHeader } from "../../_components/drawer/header.drawer";
import { TextValidatorHcs } from "../../_components/textValidator/textValidatorHcs";
import { toastr } from "../../_components/toastr/toastr";
import { handleErrorResponse } from "../../_helpers/handleResponse.helper";
import { userRacingLicenseService } from "../../_services/userRacingLicense.service";
import { useDrawerStyles } from "../../_styles/drawer.styles";

export const UserProfileCompetitionLicenseManageDrawer = (params: { onSave(): void, onClose(): void, isMinor: boolean }) => {

    const navigate = useNavigate();
    const { licenseId, userId } = useParams<{ licenseId: string, userId: string }>();

    const resetData = () => ({
        id: 0,
        name: "",
        value: 0,
        code: "",
        issueDate: "",
        expiryDate: "",
        updateExpiryOnAllLicenses: false
    });


    const [data, setData] = useState<any>(resetData);

    useEffect(() => {
        if (licenseId != null)
            fetchData();
        else
            setData(resetData);
    }, [licenseId]);

    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    function fetchData() {
        if (!licenseId) return;

        setIsLoading(true)
        return userRacingLicenseService
            .getById(Number(licenseId))
            .then(
                (json) => {
                    setData({ ...json, expiryDate: moment(json.expiryDate).format('YYYY-MM-DD'), issueDate: moment(json.issueDate).format('YYYY-MM-DD') });
                }, handleErrorResponse
            )
            .finally(() => {
                setIsLoading(false);
            });
    }

    function submit(e: any) {
        e.preventDefault();
        setIsSaving(true);

        return userRacingLicenseService
            .submit({ ...data, id: licenseId, userId: userId })
            .then(
                () => {
                    toastr.success("Saved successfully");
                    params.onSave();
                }, handleErrorResponse
            )
            .finally(() => setIsSaving(false));
    }

    function disable() {
        if (!licenseId) return;
        setIsLoading(true);
        return userRacingLicenseService
            .disable(Number(licenseId))
            .then(
                (d: any) => {
                    toastr.success("Disabled successfully");
                    fetchData();
                    params.onSave();
                }, handleErrorResponse
            )
            .finally(() => setIsLoading(false));
    }

    function handleChange(event: any) {
        setData((_data: any) => ({ ..._data, [event.target.name]: event.target.value }))
    };

    function handleChangeSwitch(event: any) {
        setData((__data: any) => { return { ...__data, [event.target.name]: !__data[event.target.name] } });
    };

    function increaseYearByOne() {
        setData((_data: any) => {
            let expiryDate = moment(_data.expiryDate || new Date()).add(1, 'y').format('YYYY-MM-DD')
            return { ..._data, expiryDate: expiryDate }
        })
    }

    return (

        <Drawer
            anchor={"right"}
            variant="persistent"
            open={true}
            ModalProps={{
                keepMounted: true
            }}
        >

            <div
                tabIndex={0}
                role="button">

                <div>

                    <DrawerHeader name={(!!licenseId) ? "Update User Licence" : "Create User Licence"} onClose={params.onClose} />

                    <ValidatorForm
                        onSubmit={submit}
                        noValidate>



                        <Grid container spacing={1} sx={useDrawerStyles.container}>
                            <Grid item xs={12}>

                                <RacingLicenseTypeAutoComplete
                                    itemId={data.racingLicenseTypeId}
                                    name={"racingLicenseTypeId"}
                                    isRequired
                                    hasParent={false}
                                    onItemChange={handleChange} />
                            </Grid>

                            <Grid item xs={12}>
                                <RacingLicenseGradeAutoComplete
                                    isMinor={params.isMinor}
                                    itemId={data.racingLicenseGradeId}
                                    name={"racingLicenseGradeId"}
                                    isRequired
                                    parentId={data.racingLicenseTypeId}
                                    hasParent={true}
                                    onItemChange={handleChange} />

                            </Grid>

                            <Grid item xs={12}>
                                <TextValidatorHcs
                                    key="licenseNumber"
                                    name="licenseNumber"
                                    label="License Number"
                                    required
                                    helperText="Set to 0 if you want it to be auto generated"
                                    fullWidth
                                    type="number"
                                    validators={['required']}
                                    errorMessages={['required field']}
                                    value={data.licenseNumber}
                                    onChange={handleChange}
                                />
                            </Grid>


                            <Grid item xs={12}>
                                <TextValidatorHcs
                                    key="issueDate"
                                    name="issueDate"
                                    label="Issue Date"
                                    required
                                    fullWidth
                                    type="date"
                                    validators={['required']}
                                    errorMessages={['required field']}
                                    value={data.issueDate}
                                    onChange={handleChange}
                                />
                            </Grid>

                            <Grid item xs={10}>
                                <TextValidatorHcs
                                    key="expiryDate"
                                    name="expiryDate"
                                    label="Expiry Date"
                                    type="date"
                                    required
                                    fullWidth
                                    validators={['required', 'minStringLength:1']}
                                    errorMessages={['required field', "Field cannot be empty"]}
                                    value={data.expiryDate}
                                    onChange={handleChange}
                                />
                            </Grid>

                            <Grid item xs={2}>
                                <PrimaryButton
                                    isSaving={isSaving}
                                    isLoading={isLoading}
                                    type="button"
                                    variant="primary" onClick={increaseYearByOne}>{"+"}
                                </PrimaryButton>
                            </Grid>
                            {!!data.id &&

                                <Grid item xs={12}>
                                    <FormControlLabel
                                        control={<Switch checked={data.isCorrectAnswer}
                                            onChange={handleChangeSwitch}
                                            name="updateExpiryOnAllLicenses" />}
                                        label="Update expiry date on all licenses"
                                    />
                                </Grid>
                            }
                        </Grid>

                        <Grid container spacing={1} sx={useDrawerStyles.container}>

                            <Grid item xs={(data.id === 0 ? 12 : 6)}>
                                <PrimaryButton
                                    isSaving={isSaving}
                                    isLoading={isLoading}
                                    type="submit"
                                    variant="save">{"Save"}
                                </PrimaryButton>
                            </Grid>

                            {data.id > 0 &&
                                <Grid item xs={6}>
                                    <PrimaryButton
                                        type="button"
                                        variant="danger"
                                        disabled={isLoading}
                                        onClick={disable}
                                        icon="block"
                                    >
                                        Disable
                                    </PrimaryButton>
                                </Grid>
                            }

                        </Grid>

                    </ValidatorForm>
                </div>
            </div>
        </Drawer >
    );
};